import { EventEmitter, Injectable } from '@angular/core';
import {
    BehaviorSubject,
    filter,
    interval,
    mergeMap,
    Observable,
    Subject,
    Subscription,
    tap,
} from 'rxjs';
import { MessageService } from 'src/app/message/services/message.service';
import {
    emptyTrainingTreeNode,
    TrainingTreeNode,
} from '../models/training-tree-node';
import { switchMap, takeUntil } from 'rxjs/operators';
import { ProjectService } from 'src/app/project/services/project.service';
import { TrainingService } from 'src/app/shared/services/training.service';
import { TrainingDTO } from 'src/app/shared/models/training-dto';
import { TrainingStatus } from 'src/app/shared/models/training-status';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import {
    TRAINING_NODE_ERROR,
    TRAINING_NODE_INPUT,
} from '../models/tree-drawing-properties';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class TreeStateService {
    $unsubscribeTrigger: Subject<void> = new Subject<void>();
    startedTrainings: string[] = [];
    private _selectedTreeNode = new BehaviorSubject<TrainingTreeNode>(
        emptyTrainingTreeNode
    );
    private _treeState = new BehaviorSubject<TrainingTreeNode>(
        emptyTrainingTreeNode
    );
    private _selectedTrainingId = new BehaviorSubject<string>('');

    public isMenuOpen = false;
    public projectId = '';
    public trainingNameValue = '';
    public isTrainingNameEditable = false;
    private cursorSelectionStartTrainingName: number = 0;
    private cursorSelectionEndTrainingName: number = 0;

    public onTrainingCloned = new EventEmitter();

    private treeUpdateSubscription$: Subscription = new Subscription();

    private treeStateUpdateInterval: number = 10000;
    private readonly handleBlurEvent = ($event: FocusEvent) => {
        this.updateTrainingName($event);
    };
    private readonly handleKeyEvent = ($event: KeyboardEvent) => {
        const key: string = $event.key;
        switch (key) {
            case 'Enter':
                this.updateTrainingName($event);
                break;
            case 'Escape':
                this.cancelTrainingNameUpdate($event);
                break;
        }
    };
    private readonly handleInputEvent = (_$event: InputEvent) => {
        this.handleTrainingNameError();
    };

    get selectedTreeNode(): TrainingTreeNode {
        return this._selectedTreeNode.value;
    }

    get selectedTreeNode$(): Observable<TrainingTreeNode> {
        return this._selectedTreeNode.asObservable();
    }

    set selectedTreeNode(newValue: TrainingTreeNode) {
        this._selectedTreeNode.next(newValue);
    }

    set treeState(newTree: TrainingTreeNode) {
        this._treeState.next(newTree);
    }

    get treeState(): TrainingTreeNode {
        return this._treeState.value;
    }

    get treeState$(): Observable<TrainingTreeNode> {
        return this._treeState.asObservable();
    }

    set selectedTrainingId(value: string) {
        this._selectedTrainingId.next(value);
    }

    get selectedTrainingId(): string {
        return this._selectedTrainingId.value;
    }

    public getTrainingNameInputField(): HTMLInputElement {
        return <HTMLInputElement>document.getElementById(TRAINING_NODE_INPUT);
    }

    public getTrainingNameErrorElement(): HTMLSpanElement {
        return <HTMLSpanElement>document.getElementById(TRAINING_NODE_ERROR);
    }

    private treeHasRunningTraining: boolean = false;

    constructor(
        private trainingService: TrainingService,
        private messageService: MessageService,
        private projectService: ProjectService,
        private translate: TranslateService
    ) {}

    private resetCursorPositionTrainingName(): void {
        this.cursorSelectionStartTrainingName = 0;
        this.cursorSelectionEndTrainingName = 0;
    }

    private setCursorPositionTrainingName(position: number): void {
        this.cursorSelectionStartTrainingName = position;
        this.cursorSelectionEndTrainingName = position;
    }

    private saveCursorPositionTrainingName(
        inputElement: HTMLInputElement
    ): void {
        this.cursorSelectionStartTrainingName = inputElement.selectionStart;
        this.cursorSelectionEndTrainingName = inputElement.selectionEnd;
    }

    private loadCursorPositionTrainingName(
        inputElement: HTMLInputElement
    ): void {
        inputElement.selectionStart = this.cursorSelectionStartTrainingName;
        inputElement.selectionEnd = this.cursorSelectionEndTrainingName;
    }

    public startTreeUpdates() {
        this.treeUpdateSubscription$ = interval(this.treeStateUpdateInterval)
            .pipe(
                filter(() => this.treeHasRunningTraining),
                switchMap(() => this.refreshTreeState())
            )
            .subscribe();
    }

    public cloneTreeNode(nodeUuid: string, cloneSuffix: string): void {
        this.trainingService
            .clone(nodeUuid, cloneSuffix)
            .pipe(takeUntil(this.$unsubscribeTrigger))
            .subscribe({
                next: (clonedTraining: TrainingDTO) => {
                    this.currentTree(this.projectId).subscribe(
                        (updatedRoot: TrainingTreeNode) => {
                            this.treeState = updatedRoot;
                            this.selectedTreeNode = TrainingTreeNode.findInTree(
                                updatedRoot,
                                clonedTraining.uuid
                            );
                            this.onTrainingCloned.emit();
                        }
                    );
                },
            });
    }

    public deleteTreeNode(nodeUuid: string): void {
        const parentOfDeletion = TrainingTreeNode.findParentInTree(
            this.treeState,
            nodeUuid
        );
        this.trainingService
            .delete(nodeUuid)
            .pipe(takeUntil(this.$unsubscribeTrigger))
            .subscribe({
                next: () => {
                    this.currentTree(this.projectId)
                        .pipe(takeUntil(this.$unsubscribeTrigger))
                        .subscribe((updatedRoot: TrainingTreeNode) => {
                            this.treeState = updatedRoot;
                            setTimeout(() => {
                                this.selectedTreeNode = parentOfDeletion;
                            }, 1100);
                        });
                },
                error: (error) => {
                    if (error.status === 400) {
                        if (error.error === null) {
                            this.messageService.displayTranslatedErrorMessage(
                                'splitScreen.components.tree.errorMessages.trainingDeletionNotLeaf',
                                {}
                            );
                        } else if (
                            error.error.message.includes(
                                "Cannot delete a training that hasn't been billed."
                            )
                        ) {
                            this.messageService.displayTranslatedErrorMessage(
                                'splitScreen.components.tree.errorMessages.trainingDeletionBilling',
                                {}
                            );
                        }
                    } else {
                        this.messageService.displayTranslatedErrorMessage(
                            'default.errorMessage',
                            {}
                        );
                    }
                    throw error;
                },
            });
    }

    downloadTrainingSkill(trainingId: string): void {
        this.trainingService.downloadSkill(trainingId).subscribe({
            next: (response: HttpResponse<Blob>) => {
                const contentDisposition = response.headers.get(
                    'content-disposition'
                );
                const downloadURL = window.URL.createObjectURL(response.body);
                const link = document.createElement('a');
                link.href = downloadURL;
                link.download =
                    this.getTrainingSkillFileName(contentDisposition);
                link.click();
                window.URL.revokeObjectURL(downloadURL);
            },
            error: (error: HttpErrorResponse) => {
                let messageKey: string =
                    'splitScreen.components.contextMenu.canNotDownloadSkill';
                if (error.status === 404) {
                    messageKey =
                        'splitScreen.components.contextMenu.canNotFindSkillFile';
                }
                this.messageService.displayTranslatedErrorMessage(
                    messageKey,
                    null
                );
            },
        });
    }

    private getTrainingSkillFileName(contentDisposition: string): string {
        return contentDisposition
            ? contentDisposition
                  .split(';')[1]
                  .trim()
                  .split('=')[1]
                  .replace(/\"/g, '')
            : 'file';
    }

    // TODO: fix initial loading
    public refreshTreeState(): Observable<TrainingTreeNode> {
        return this.currentTree(this.projectId).pipe(
            tap((tree: TrainingTreeNode) => {
                this.setTreeHasRunningTraining(this.hasRunningTraining(tree));
                this._treeState.next(tree);
            }),
            tap((tree: TrainingTreeNode) => {
                if (this.selectedTrainingId) {
                    this.selectedTreeNode = TrainingTreeNode.findInTree(
                        tree,
                        this.selectedTrainingId
                    );
                } else {
                    this.selectedTreeNode = tree;
                }
            })
        );
    }

    setTreeHasRunningTraining(treeHasRunningTraining: boolean) {
        this.treeHasRunningTraining = treeHasRunningTraining;
    }

    hasRunningTraining(tree: TrainingTreeNode) {
        const runningStatuses = [
            TrainingStatus.RUNNING,
            TrainingStatus.QUEUED,
            TrainingStatus.CANCELLING,
            TrainingStatus.PREPARING,
        ];
        if (runningStatuses.includes(tree.status)) {
            return true;
        }
        for (let child of tree.children) {
            if (this.hasRunningTraining(child)) {
                return true;
            }
        }
        return false;
    }

    private updateSelectedTreeNodeName(newName: string) {
        if (this.selectedTreeNode.name !== newName) {
            this.notifyTrainingService(newName).subscribe();
        }
        //Always do this to re-render
        this.selectedTreeNode = TrainingTreeNode.updateNodeName(
            this.treeState,
            this.selectedTreeNode.uuid,
            newName
        );
    }

    public updateTrainingName(_event: FocusEvent | KeyboardEvent) {
        const newName = this.getTrainingNameInputField().value.trim();
        if (!this.getTrainingNameErrorMessage(newName)) {
            this.isTrainingNameEditable = false;
            this.trainingNameValue = '';
            this.resetCursorPositionTrainingName();

            this.updateSelectedTreeNodeName(newName);
        }
    }

    public prepareTrainingNameInputFieldForRedraw() {
        let oldInputField = this.getTrainingNameInputField();
        if (oldInputField) {
            this.trainingNameValue = oldInputField.value;
            this.saveCursorPositionTrainingName(oldInputField);
            //Dont blur-save on redraw
            oldInputField.removeEventListener('blur', this.handleBlurEvent);
        }
    }

    public loadTrainingNameInputFieldAfterRedraw() {
        // Focus Training Name Input Field and set Cursor to end of input field
        let inputField = this.getTrainingNameInputField();
        if (inputField) {
            inputField.focus();
            this.loadCursorPositionTrainingName(inputField);
            inputField.addEventListener('blur', this.handleBlurEvent);
            inputField.addEventListener('keyup', this.handleKeyEvent);
            inputField.addEventListener('input', this.handleInputEvent);
            this.handleTrainingNameError();
        }
    }

    public cancelTrainingNameUpdate(_event: KeyboardEvent) {
        this.trainingNameValue = '';
        this.isTrainingNameEditable = false;
        this.resetCursorPositionTrainingName();

        //Redraw
        this._selectedTreeNode.next(this.selectedTreeNode);
    }

    public prepareTrainingNameEditing(currentName: string) {
        this.trainingNameValue = currentName;
        this.isTrainingNameEditable = true;
        this.setCursorPositionTrainingName(currentName.length);
    }

    public handleTrainingNameError() {
        const errorElement: HTMLSpanElement =
            this.getTrainingNameErrorElement();
        const input: HTMLInputElement = this.getTrainingNameInputField();
        const newTrainingName = input.value;
        const potentialErrorMessage =
            this.getTrainingNameErrorMessage(newTrainingName);
        if (potentialErrorMessage) {
            errorElement.textContent = potentialErrorMessage;
            errorElement.setAttribute('class', 'active');
            input.className += ' input-field-invalid';
        } else {
            errorElement.textContent = '';
            errorElement.removeAttribute('class');
            input.className = 'input-field';
        }
    }

    public isSelectedTrainingNameValid(): boolean {
        const input: HTMLInputElement = this.getTrainingNameInputField();
        if (!input) {
            return true;
        }
        return (
            this.getTrainingNameErrorMessage(input.value) === null ||
            this.getTrainingNameErrorMessage(input.value) === undefined
        );
    }

    private getTrainingNameErrorMessage(newName: string): string {
        if (newName.length < 2) {
            return this.translate.instant(
                'splitScreen.components.questSplitMenu.minlengthError'
            );
        } else if (newName.length > 32) {
            return this.translate.instant(
                'splitScreen.components.questSplitMenu.maxlengthError'
            );
        }
        const regex = new RegExp('[^A-Za-z0-9-_. ]');
        return regex.test(newName)
            ? this.translate.instant(
                  'splitScreen.components.questSplitMenu.patternError'
              )
            : null;
    }

    private notifyTrainingService(newName: string): Observable<TrainingDTO> {
        return this.trainingService.get(this.selectedTreeNode.uuid).pipe(
            mergeMap((training: TrainingDTO) => {
                training.name = newName;
                return this.trainingService.update(training);
            })
        );
    }

    public getTrainingOfSelectedNode(): Observable<TrainingDTO> {
        return this.trainingService.get(this.selectedTreeNode.uuid);
    }

    cleanup() {
        this.$unsubscribeTrigger.next();
        this.isMenuOpen = false;
        this.selectedTreeNode = emptyTrainingTreeNode;
        this.treeState = emptyTrainingTreeNode;
        this.treeUpdateSubscription$.unsubscribe();
    }

    currentTree(projectId: string) {
        return this.projectService.getTrainingTree(projectId);
    }
}
